import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search } from "lucide-react";

import CompanyAdminLayout from "../../components/CompanyAdminLayout";
import { chatApi, chatMediaUrl } from "../../lib/chatApi";
import useChatSocket from "../../hooks/useChatSocket";
import { uploadChatMedia } from "../../lib/chatUpload";
import { ChatHeader, ChatInput, ChatMessageList } from "../../components/chat";
import {
  ensureNotificationPermission,
  notifyIncoming,
  playBeep,
} from "../../lib/notify";
import { cn } from "../../lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";

const CONTACTS_PAGE_SIZE = 50;

function sortCustomersByActivity(list) {
  return [...list].sort((a, b) => {
    const at = a?.thread?.last_message_at
      ? new Date(a.thread.last_message_at).getTime()
      : 0;
    const bt = b?.thread?.last_message_at
      ? new Date(b.thread.last_message_at).getTime()
      : 0;
    return bt - at;
  });
}

/** Local warm/cold filter (API returns all; has_customer_message on each row). */
function filterCustomersByLead(list, leadFilter) {
  if (leadFilter === "warm") {
    return list.filter((c) => !!c?.has_customer_message);
  }
  if (leadFilter === "cold") {
    return list.filter((c) => !c?.has_customer_message);
  }
  return list;
}

function mapInfiniteCustomerPages(old, mapRow) {
  if (!old?.pages) return old;
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      customers: page.customers.map(mapRow),
    })),
  };
}

const ChatsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // navigation handled by sidebar group links
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Request notification permission once (non-blocking)
    ensureNotificationPermission();
  }, []);

  const [activeGroupId, setActiveGroupId] = useState(null);

  // Groups are loaded/sorted (and unread badges shown) in CompanyAdminLayout sidebar.

  const queryClient = useQueryClient();

  const [customerSearch, setCustomerSearch] = useState("");
  const [debouncedCustomerSearch, setDebouncedCustomerSearch] = useState("");
  const activeGroupIdRef = useRef(activeGroupId);
  const openCustomerIdFromUrlRef = useRef(null);
  const chatActivityDebounceRef = useRef(null);
  const [leadsFilter, setLeadsFilter] = useState("all"); // "all" | "warm" | "cold"
  const [activeCustomerId, setActiveCustomerId] = useState(null);

  const [threadByCustomerId, setThreadByCustomerId] = useState({});
  const [activeThreadId, setActiveThreadId] = useState(null);

  const [messagesByThread, setMessagesByThread] = useState({});
  const [draft, setDraft] = useState("");

  // Typing indicator state
  const [remoteTyping, setRemoteTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);

  // Block state
  const [threadBlocked, setThreadBlocked] = useState(false);
  const [blockedByName, setBlockedByName] = useState(null);

  // Mute state (local per session)
  const [muted, setMuted] = useState(false);
  const mutedRef = useRef(false);

  const chatListRef = useRef(null);
  const activeCustomerIdRef = useRef(activeCustomerId);
  const [showJumpToBottom, setShowJumpToBottom] = useState(false);

  const [userAtBottom, setUserAtBottom] = useState(true);

  useEffect(() => {
    activeCustomerIdRef.current = activeCustomerId;
  }, [activeCustomerId]);

  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    const run = () => {
      const el = chatListRef.current;
      if (!el) return;
      el.scrollTop = el.scrollHeight;
      setShowJumpToBottom(false);
    };
    requestAnimationFrame(() => requestAnimationFrame(run));
  }, []);

  /** Call after state updates that add messages so scroll runs after React has rendered. */
  const scheduleScrollToBottom = useCallback(
    (delay = 80) => {
      setTimeout(scrollToBottom, delay);
    },
    [scrollToBottom],
  );

  const isNearBottom = useCallback(() => {
    const el = chatListRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);

  const [uploadPct, setUploadPct] = useState(null);
  const [sending, setSending] = useState(false);

  const activeMessages = useMemo(() => {
    if (!activeThreadId) return [];
    return messagesByThread[String(activeThreadId)] || [];
  }, [messagesByThread, activeThreadId]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gid = params.get("group_id");
    setActiveGroupId(gid ? String(gid) : null);
    // Clear active customer when switching groups (but not when only customer param changes)
    setActiveCustomerId(null);
    setActiveThreadId(null);
  }, [location.search]);

  // Auto-open a specific customer passed via ?customer=<id> from Customers page
  const openCustomerIdFromUrl = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("customer") || null;
  }, [location.search]);

  useEffect(() => {
    activeGroupIdRef.current = activeGroupId;
  }, [activeGroupId]);

  useEffect(() => {
    openCustomerIdFromUrlRef.current = openCustomerIdFromUrl;
  }, [openCustomerIdFromUrl]);

  const customersQueryKey = useMemo(
    () =>
      [
        "admin-chat-customers",
        activeGroupId || "__none__",
        debouncedCustomerSearch.trim(),
        openCustomerIdFromUrl || "",
      ],
    [activeGroupId, debouncedCustomerSearch, openCustomerIdFromUrl],
  );

  const customersQueryKeyRef = useRef(customersQueryKey);
  useEffect(() => {
    customersQueryKeyRef.current = customersQueryKey;
  }, [customersQueryKey]);

  const {
    data: customersInfiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending: contactsListPending,
    isFetched: customersListFetched,
  } = useInfiniteQuery({
    queryKey: customersQueryKey,
    enabled: !!activeGroupId,
    initialPageParam: 1,
    staleTime: 45_000,
    queryFn: async ({ pageParam, signal }) => {
      const groupId = activeGroupIdRef.current;
      if (!groupId) {
        return {
          customers: [],
          has_more: false,
          total: 0,
        };
      }
      const q = debouncedCustomerSearch.trim() || undefined;
      const res = await chatApi.getCustomers(
        groupId,
        pageParam,
        CONTACTS_PAGE_SIZE,
        { q, signal },
      );
      let list = res.data.customers || [];
      const urlCust = openCustomerIdFromUrlRef.current;
      if (pageParam === 1 && urlCust) {
        const match = list.find(
          (c) =>
            String(c._id) === String(urlCust) ||
            String(c.user_id) === String(urlCust),
        );
        if (!match) {
          try {
            const r2 = await chatApi.getCustomers(
              groupId,
              1,
              CONTACTS_PAGE_SIZE,
              { customer_id: urlCust, signal },
            );
            const extra = (r2.data.customers || [])[0];
            if (extra) {
              const idStr = String(extra._id);
              list = [extra, ...list.filter((c) => String(c._id) !== idStr)];
            }
          } catch {
            /* ignore */
          }
        }
      }
      const activeId = activeCustomerIdRef.current;
      const normalized = list.map((c) => ({
        ...c,
        unread_count:
          String(c._id) === String(activeId) ? 0 : (c.unread_count ?? 0),
      }));
      return {
        customers: normalized,
        has_more: !!res.data.has_more,
        total: res.data.total ?? normalized.length,
      };
    },
    getNextPageParam: (lastPage, allPages) =>
      lastPage.has_more ? allPages.length + 1 : undefined,
    onError: () => {
      toast.error("Failed to load customers for this group");
    },
  });

  const customers = useMemo(() => {
    const pages = customersInfiniteData?.pages;
    if (!pages?.length) return [];
    const seen = new Set();
    const out = [];
    for (const p of pages) {
      for (const c of p.customers) {
        const id = String(c._id);
        if (!seen.has(id)) {
          seen.add(id);
          out.push(c);
        }
      }
    }
    return sortCustomersByActivity(out);
  }, [customersInfiniteData]);

  const activeCustomer = useMemo(
    () =>
      customers.find((c) => String(c._id) === String(activeCustomerId)) || null,
    [customers, activeCustomerId],
  );

  const contactsTotal = customersInfiniteData?.pages?.[0]?.total ?? 0;
  const contactsHasMore = !!hasNextPage;

  const invalidateCustomersDebounced = useCallback(() => {
    const gid = activeGroupIdRef.current;
    if (!gid) return;
    clearTimeout(chatActivityDebounceRef.current);
    chatActivityDebounceRef.current = setTimeout(() => {
      queryClient.invalidateQueries({
        queryKey: ["admin-chat-customers", gid],
      });
    }, 600);
  }, [queryClient]);

  const invalidateCustomersDebouncedRef = useRef(invalidateCustomersDebounced);
  useEffect(() => {
    invalidateCustomersDebouncedRef.current = invalidateCustomersDebounced;
  }, [invalidateCustomersDebounced]);

  useEffect(() => {
    const t = setTimeout(() => {
      const v = customerSearch.trim();
      setDebouncedCustomerSearch(v);
    }, 350);
    return () => clearTimeout(t);
  }, [customerSearch]);

  /** Deep link: ?customer= only — resolve group then replace URL (avoids wrong first group). */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gid = params.get("group_id");
    const cust = params.get("customer");
    if (!cust || gid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await chatApi.getCustomerOpen(cust);
        if (cancelled) return;
        const { group_id, customer } = res.data || {};
        if (group_id && customer) {
          navigate(
            `/admin/chats?group_id=${encodeURIComponent(group_id)}&customer=${encodeURIComponent(cust)}`,
            { replace: true },
          );
        } else {
          toast.info(
            "Could not open chat — customer not found or not in your groups.",
          );
          navigate("/admin/chats", { replace: true });
        }
      } catch {
        if (!cancelled) {
          toast.error("Could not resolve chat for that customer.");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search, navigate]);

  const token = localStorage.getItem("company_admin_token");

  const mergeIncomingMessage = useCallback((threadId, msg) => {
    if (!threadId || !msg) return;
    setMessagesByThread((prev) => {
      const key = String(threadId);
      const existing = prev[key] ? [...prev[key]] : [];

      // Deduplicate by server message id OR client_id
      const idxById = msg.id
        ? existing.findIndex((m) => Number(m.id) === Number(msg.id))
        : -1;
      if (idxById >= 0) {
        const next = [...existing];
        next[idxById] = { ...next[idxById], ...msg };
        return { ...prev, [key]: next };
      }

      if (msg.client_id) {
        const idxByClient = existing.findIndex(
          (m) => m.client_id && m.client_id === msg.client_id,
        );
        if (idxByClient >= 0) {
          const next = [...existing];
          next[idxByClient] = { ...next[idxByClient], ...msg };
          return { ...prev, [key]: next };
        }
      }

      return { ...prev, [key]: [...existing, msg] };
    });
  }, []);

  const broadcastChatActivity = useCallback(() => {
    try {
      window.dispatchEvent(new Event("chat:activity"));
    } catch (e) {
      // ignore
    }
  }, []);

  // Bump customer row; list order updates via sorted flatten over query cache
  const bumpCustomerThread = useCallback(
    (threadId, at, preview, senderRole, senderName, opts = {}) => {
      if (!threadId) return false;
      const ts = at || new Date().toISOString();
      const activeId = activeCustomerIdRef.current;
      let found = false;
      const key = customersQueryKeyRef.current;
      queryClient.setQueryData(key, (old) => {
        if (!old?.pages) return old;
        const pages = old.pages.map((page) => ({
          ...page,
          customers: page.customers.map((c) => {
            if (String(c.thread_id) !== String(threadId)) return c;
            found = true;
            const isCustomer = senderRole === "customer";
            const isActive = String(c._id) === String(activeId);
            const unreadDelta =
              opts.incrementUnread && isCustomer && !isActive ? 1 : 0;
            return {
              ...c,
              has_customer_message: isCustomer ? true : c.has_customer_message,
              unread_count: isActive
                ? 0
                : Math.max(0, (c.unread_count ?? 0) + unreadDelta),
              thread: {
                ...c.thread,
                last_message_at: ts,
                last_message_preview:
                  preview ?? c?.thread?.last_message_preview,
                last_sender_role: senderRole ?? c?.thread?.last_sender_role,
                last_sender_name: senderName ?? c?.thread?.last_sender_name,
              },
            };
          }),
        }));
        return { ...old, pages };
      });
      return found;
    },
    [queryClient],
  );

  const handleWsEvent = useCallback(
    (evt) => {
      if (!evt || typeof evt !== "object") return;
      if (evt.type === "message_created") {
        const tid = evt.thread_id || activeThreadId;
        const msg = evt.message;
        if (msg?.sender_role === "staff") {
          bumpCustomerThread(
            tid,
            msg?.created_at,
            msg?.text,
            "staff",
            msg?.sender_name || "Admin",
          );
          return;
        }

        const shouldJump = tid === activeThreadId && isNearBottom();
        mergeIncomingMessage(tid, msg);
        const senderName =
          msg?.sender_name ||
          (msg?.sender_role === "customer"
            ? customers.find((c) => String(c.thread_id) === String(tid))
                ?.name || "Customer"
            : "Admin");
        const bumped = bumpCustomerThread(
          tid,
          msg?.created_at,
          msg?.text,
          msg?.sender_role,
          senderName,
          { incrementUnread: msg?.sender_role === "customer" },
        );
        if (msg?.sender_role === "customer" && !bumped) {
          invalidateCustomersDebouncedRef.current?.();
        }

        if (tid === activeThreadId) {
          if (userAtBottom) {
            scheduleScrollToBottom();
            // If user is viewing the bottom and tab is active, mark as read immediately
            if (!document.hidden && msg?.id) {
              chatApi.markRead(tid, msg.id).catch(() => {});
              // Clear unread count for active customer immediately
              if (activeCustomerId) {
                const key = customersQueryKeyRef.current;
                queryClient.setQueryData(key, (old) =>
                  mapInfiniteCustomerPages(old, (c) =>
                    String(c._id) === String(activeCustomerId)
                      ? { ...c, unread_count: 0 }
                      : c,
                  ),
                );
              }
            }
          } else {
            setShowJumpToBottom(true);
          }
        }

        // Notify only for incoming messages when tab inactive (unless muted)
        if (
          document.hidden &&
          msg &&
          msg.sender_role === "customer" &&
          !mutedRef.current
        ) {
          playBeep();
          notifyIncoming({
            title: "New customer message",
            body: msg.text || "Attachment",
          });
        }

        // Refresh sidebar unread counts in real-time
        broadcastChatActivity();
      }
      if (evt.type === "message_deleted_for_me") {
        if (!evt.message_id) return;
        setMessagesByThread((prev) => {
          const key = String(activeThreadId);
          const list = prev[key]
            ? prev[key].filter((m) => Number(m.id) !== Number(evt.message_id))
            : [];
          return { ...prev, [key]: list };
        });
      }
      if (evt.type === "message_deleted_for_all") {
        if (!evt.message_id) return;
        setMessagesByThread((prev) => {
          const key = String(activeThreadId);
          const list = prev[key]
            ? prev[key].map((m) => {
                if (Number(m.id) !== Number(evt.message_id)) return m;
                // Soft delete: admin still sees content with deleted_by_name note
                if (evt.mode === "soft") {
                  return {
                    ...m,
                    deleted_for_all: true,
                    deleted_mode: "soft",
                    deleted_by_name: evt.deleted_by_name || "Staff",
                  };
                }
                // Hard delete: content removed for everyone
                return {
                  ...m,
                  deleted_for_all: true,
                  deleted_mode: evt.mode,
                  masked: true,
                  text: null,
                  media: [],
                  media_ids: [],
                };
              })
            : [];
          return { ...prev, [key]: list };
        });
      }
      if (evt.type === "message_delivered") {
        const tid =
          evt.thread_id != null
            ? String(evt.thread_id)
            : String(activeThreadId);
        const ids = new Set(
          (evt.message_ids || []).map((id) => Number(id)).filter(Boolean),
        );
        if (ids.size === 0) return;
        setMessagesByThread((prev) => {
          const list = prev[tid];
          if (!list) return prev;
          const next = list.map((m) => {
            if (m.sender_role !== "staff") return m;
            if (!ids.has(Number(m.id))) return m;
            return {
              ...m,
              status: "delivered",
              delivered_at: evt.delivered_at,
            };
          });
          return { ...prev, [tid]: next };
        });
      }
      if (evt.type === "message_read") {
        const tid =
          evt.thread_id != null
            ? String(evt.thread_id)
            : String(activeThreadId);
        const lastId =
          evt.last_read_message_id != null
            ? Number(evt.last_read_message_id)
            : null;
        if (lastId == null) return;
        setMessagesByThread((prev) => {
          const list = prev[tid];
          if (!list) return prev;
          const next = list.map((m) => {
            if (m.sender_role !== "staff") return m;
            if (Number(m.id) > lastId) return m;
            return { ...m, status: "read", read_at: evt.read_at };
          });
          return { ...prev, [tid]: next };
        });
      }
      if (evt.type === "typing" && evt.sender_role === "customer") {
        setRemoteTyping(true);
        if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
        typingTimeoutRef.current = setTimeout(
          () => setRemoteTyping(false),
          3000,
        );
      }
      if (evt.type === "thread_blocked") {
        setThreadBlocked(!!evt.blocked);
        setBlockedByName(evt.blocked ? evt.blocked_by_name || "Staff" : null);
      }
      if (evt.type === "thread_deleted" && evt.thread_id != null) {
        const deletedId = String(evt.thread_id);
        if (deletedId === String(activeThreadId)) {
          setActiveThreadId(null);
          if (activeCustomerId) {
            setThreadByCustomerId((prev) => {
              const next = { ...prev };
              delete next[String(activeCustomerId)];
              return next;
            });
            setActiveCustomerId(null);
          }
          setMessagesByThread((prev) => {
            const next = { ...prev };
            delete next[deletedId];
            return next;
          });
          toast.info("This chat was deleted");
          broadcastChatActivity();
        }
      }
    },
    [
      activeThreadId,
      activeCustomerId,
      mergeIncomingMessage,
      broadcastChatActivity,
      userAtBottom,
      scheduleScrollToBottom,
      bumpCustomerThread,
      customers,
      isNearBottom,
      queryClient,
    ],
  );

  const { connected: wsConnected, sendJson: wsSendJson } = useChatSocket({
    threadId: activeThreadId,
    token,
    enabled: !!activeGroupId && !!token,
    listenAllThreads: true,
    onEvent: handleWsEvent,
  });

  const handleMarkAllRead = useCallback(async () => {
    try {
      await chatApi.markAllRead();
      const key = customersQueryKeyRef.current;
      queryClient.setQueryData(key, (old) =>
        mapInfiniteCustomerPages(old, (c) => ({ ...c, unread_count: 0 })),
      );
      broadcastChatActivity();
      toast.success("All chats marked as read");
    } catch {
      toast.error("Failed to mark all as read");
    }
  }, [broadcastChatActivity, queryClient]);

  const fetchGroups = async () => {
    // groups are shown in the left sidebar tree view
    return;
  };

  // Helper: clear unread count locally for active customer and refresh sidebar
  const clearUnreadForActive = useCallback(() => {
    const id = activeCustomerIdRef.current;
    if (id) {
      const key = customersQueryKeyRef.current;
      queryClient.setQueryData(key, (old) =>
        mapInfiniteCustomerPages(old, (c) =>
          String(c._id) === String(id) ? { ...c, unread_count: 0 } : c,
        ),
      );
    }
    broadcastChatActivity();
  }, [broadcastChatActivity, queryClient]);

  // Load thread + initial messages when selecting a customer
  useEffect(() => {
    setRemoteTyping(false);
    const run = async () => {
      if (!activeCustomerId) {
        setActiveThreadId(null);
        setThreadBlocked(false);
        setBlockedByName(null);
        return;
      }

      const key = customersQueryKeyRef.current;
      queryClient.setQueryData(key, (old) =>
        mapInfiniteCustomerPages(old, (c) =>
          String(c._id) === String(activeCustomerId)
            ? { ...c, unread_count: 0 }
            : c,
        ),
      );

      const existing = threadByCustomerId[String(activeCustomerId)];
      if (existing) {
        setActiveThreadId(existing);
        return;
      }

      setThreadBlocked(false);
      setBlockedByName(null);

      try {
        const customerId =
          activeCustomerId != null ? String(activeCustomerId) : null;
        if (!customerId) return;
        const res = await chatApi.createOrGetThread(customerId);
        const thread = res.data.thread;
        const tid = thread?.id ?? thread?.roomid;
        if (tid == null) return;
        setThreadByCustomerId((prev) => ({
          ...prev,
          [String(activeCustomerId)]: tid,
        }));
        setActiveThreadId(tid);
        if (thread) {
          setThreadBlocked(!!thread.blocked);
          setBlockedByName(
            thread.blocked ? thread.blocked_by_name || "Staff" : null,
          );
        }
      } catch (e) {
        const msg =
          e.response?.data?.message ||
          e.message ||
          "Failed to open chat thread";
        toast.error(msg);
      }
    };
    run();
  }, [activeCustomerId, threadByCustomerId, queryClient]);

  useEffect(() => {
    const run = async () => {
      if (!activeThreadId) return;
      try {
        const res = await chatApi.getMessages(activeThreadId, { limit: 30 });

        setMessagesByThread((prev) => ({
          ...prev,
          [String(activeThreadId)]: res.data.messages || [],
        }));
        // Load block status from thread (server is source of truth after refresh)
        const thread = res.data.thread;
        if (thread != null) {
          const blocked = thread.blocked === true || thread.blocked === 1;
          setThreadBlocked(!!blocked);
          setBlockedByName(blocked ? thread.blocked_by_name || "Staff" : null);
        }
        const lastId = (res.data.messages || []).slice(-1)[0]?.id;
        if (lastId) {
          await chatApi.markRead(activeThreadId, lastId);
          clearUnreadForActive();
        }
        scheduleScrollToBottom(120);
      } catch (e) {
        toast.error("Failed to load messages");
      }
    };
    run();
  }, [activeThreadId, scheduleScrollToBottom, clearUnreadForActive]);

  useEffect(() => {
    const onActivity = () => {
      invalidateCustomersDebouncedRef.current?.();
    };
    window.addEventListener("chat:activity", onActivity);
    return () => {
      window.removeEventListener("chat:activity", onActivity);
      clearTimeout(chatActivityDebounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (!activeGroupId) return undefined;
    const gid = activeGroupId;
    const id = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["admin-chat-customers", gid] });
    }, 30000);
    return () => clearInterval(id);
  }, [activeGroupId, queryClient]);

  // Mark-as-read when staff opens the chat / returns to tab
  useEffect(() => {
    const markVisible = async () => {
      if (!activeThreadId) return;
      if (document.hidden) return;
      const lastId = (activeMessages || []).slice(-1)[0]?.id;
      if (!lastId) return;
      try {
        await chatApi.markRead(activeThreadId, lastId);
        clearUnreadForActive();
      } catch (e) {
        // ignore
      }
    };

    markVisible();
    document.addEventListener("visibilitychange", markVisible);
    return () => document.removeEventListener("visibilitychange", markVisible);
  }, [activeThreadId, activeMessages, clearUnreadForActive]);

  useEffect(() => {
    setLoading(false);
  }, []);

  // Keep selection in sync with loaded list / deep link (query already refetches on group & search)
  useEffect(() => {
    if (!activeGroupId || !customersListFetched) return;

    if (openCustomerIdFromUrl) {
      const match = customers.find(
        (c) =>
          String(c._id) === String(openCustomerIdFromUrl) ||
          String(c.user_id) === String(openCustomerIdFromUrl),
      );
      if (!match) {
        setActiveCustomerId(null);
        setActiveThreadId(null);
        return;
      }
      const current = activeCustomerIdRef.current;
      const matchId = String(match._id);
      const urlIsSelection =
        !current ||
        String(current) === matchId ||
        String(current) === String(openCustomerIdFromUrl);
      if (urlIsSelection) {
        setActiveCustomerId(match._id);
        setActiveThreadId(match.thread_id ?? match.thread?.id ?? null);
      }
      return;
    }

    const activeId = activeCustomerIdRef.current;
    if (
      activeId &&
      !customers.some((c) => String(c._id) === String(activeId))
    ) {
      setActiveCustomerId(null);
      setActiveThreadId(null);
    }
  }, [
    activeGroupId,
    openCustomerIdFromUrl,
    customers,
    customersListFetched,
  ]);

  const loadMoreContacts = useCallback(() => {
    if (!activeGroupId || !hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [activeGroupId, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const sendText = async () => {
    if (!activeThreadId) return;
    const text = (draft || "").trim();
    if (!text) return;

    // Clear typing indicator on send
    setRemoteTyping(false);

    setSending(true);
    try {
      // Optimistic UI
      const clientId = `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const optimistic = {
        id: `tmp-${Date.now()}`,
        client_id: clientId,
        sender_role: "staff",
        sender_name: "You",
        text,
        created_at: new Date().toISOString(),
      };
      mergeIncomingMessage(activeThreadId, optimistic);
      bumpCustomerThread(activeThreadId, optimistic.created_at, text, "staff", "You");
      scheduleScrollToBottom();

      setDraft("");

      const res = await chatApi.sendMessage(activeThreadId, {
        text,
        client_id: clientId,
      });
      const msg = res.data.message;
      if (msg)
        mergeIncomingMessage(activeThreadId, { ...msg, client_id: clientId });
      scheduleScrollToBottom();
      broadcastChatActivity();
    } catch (e) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
      requestAnimationFrame(() => inputRef.current?.focus?.());
    }
  };

  const sendAttachment = async (file) => {
    if (!activeThreadId || !file) return;

    const kind = (file.type || "").startsWith("image/") ? "image" : "file";
    setUploadPct(0);
    try {
      const media = await uploadChatMedia({
        file,
        kind,
        onProgress: ({ percent }) => setUploadPct(percent),
      });

      const clientId = `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const res = await chatApi.sendMessage(activeThreadId, {
        media_ids: [media.id],
        client_id: clientId,
      });
      if (res.data?.message)
        mergeIncomingMessage(activeThreadId, {
          ...res.data.message,
          client_id: clientId,
        });
      scheduleScrollToBottom();
      broadcastChatActivity();
    } catch (e) {
      toast.error("Failed to upload attachment");
    } finally {
      setUploadPct(null);
    }
  };

  const sendVoice = async (blob) => {
    if (!activeThreadId || !blob) return;

    const file = new File([blob], `voice-${Date.now()}.webm`, {
      type: blob.type || "audio/webm",
    });

    setUploadPct(0);
    try {
      const media = await uploadChatMedia({
        file,
        kind: "audio",
        onProgress: ({ percent }) => setUploadPct(percent),
      });

      const clientId = `c-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const res = await chatApi.sendMessage(activeThreadId, {
        media_ids: [media.id],
        client_id: clientId,
      });
      if (res.data?.message)
        mergeIncomingMessage(activeThreadId, {
          ...res.data.message,
          client_id: clientId,
        });
      scheduleScrollToBottom();
      broadcastChatActivity();
    } catch (e) {
      toast.error("Failed to upload voice message");
    } finally {
      setUploadPct(null);
    }
  };

  const customersInGroup = useMemo(() => {
    if (!activeGroupId) return [];
    return sortCustomersByActivity(
      filterCustomersByLead(customers, leadsFilter),
    );
  }, [customers, activeGroupId, leadsFilter]);

  return (
    <CompanyAdminLayout>
      <div className="h-[calc(100vh-2rem)] flex flex-col">
        <div className="panel border border-border flex-1 p-0 overflow-hidden flex flex-col min-h-0">
          {loading ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Loading chats...
            </div>
          ) : !activeGroupId ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Select a group from the sidebar.
            </div>
          ) : (
            <div className="flex-1 grid grid-cols-12 overflow-hidden min-h-0">
              {/* Customer list - flex column so list area scrolls */}
              <div className="col-span-4 border-r border-border bg-card flex flex-col min-h-0">
                <div className="px-4 pt-3 pb-2 border-b border-border shrink-0 space-y-2">
                  {/* Search + Mark All Read */}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        className="form-input !pl-10"
                        placeholder="Search customers"
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                      />
                    </div>

                  </div>
                  <div className="flex justify-end items-center pt-0.5">
                    <Select
                      value={leadsFilter}
                      onValueChange={setLeadsFilter}
                    >
                      <SelectTrigger
                        data-testid="leads-filter"
                        className="h-9 w-[min(100%,12rem)] gap-2 border-border bg-card px-3 text-left shadow-sm sm:w-[12.5rem]"
                        aria-label="Filter by lead type"
                      >
                        <SelectValue placeholder="Filter leads" />
                      </SelectTrigger>
                      <SelectContent align="end" sideOffset={4}>
                        <SelectItem value="all">All customers</SelectItem>
                        <SelectItem value="warm">Warm leads</SelectItem>
                        <SelectItem value="cold">Cold leads</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div
                  className="flex-1 min-h-0 overflow-y-auto"
                  onScroll={(e) => {
                    const el = e.currentTarget;
                    if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
                      loadMoreContacts();
                    }
                  }}
                >
                  {contactsListPending && (
                    <div className="p-6 text-sm text-muted-foreground text-center">
                      Loading customers...
                    </div>
                  )}
                  {!contactsListPending &&
                    customersInGroup.map((c, idx) => {
                    const active = String(activeCustomerId) === String(c._id);
                    const previewText = c?.thread?.last_message_preview;
                    const customerKey = c._id ?? `customer-${idx}`;
                    return (
                      <button
                        key={customerKey}
                        onClick={() => {
                          const customerId = c._id;
                          setActiveCustomerId(customerId);
                          const tid = c.thread_id ?? c.thread?.id;
                          setActiveThreadId(tid ?? null);
                        }}
                        className={cn(
                          "w-full text-left px-4 py-3 border-b border-border hover:bg-muted transition",
                          active && "bg-black/[0.04]",
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="font-semibold text-foreground truncate flex items-center gap-1.5">
                              {c.premium_member === 1 && (
                                <span
                                  data-testid="premium-badge"
                                  className="inline-block w-3.5 h-3.5 rounded-full bg-yellow-400 border border-yellow-500 shrink-0"
                                  title="Premium Member"
                                />
                              )}
                              {c.ai_plan === 1 && (
                                <span
                                  data-testid="ai-badge"
                                  className="inline-block w-3.5 h-3.5 rounded-full bg-gray-300 border border-gray-400 shrink-0"
                                  title="AI Plan"
                                />
                              )}
                              <span className="truncate">
                                {c.name || "Customer"}
                              </span>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">
                              {c.email}
                            </div>
                            <div className="text-xs text-muted-foreground mt-1 truncate">
                              {previewText ? (
                                <>
                                  <span className="font-medium text-[#637488]">
                                    {c?.thread?.last_sender_role === "customer"
                                      ? (c.name || "Customer")
                                      : (c?.thread?.last_sender_name || "Admin")}
                                    {": "}
                                  </span>
                                  {previewText}
                                </>
                              ) : "No messages yet"}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-2">
                            {c.unread_count ? (
                              <span className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-destructive text-white text-[11px] font-bold">
                                {c.unread_count}
                              </span>
                            ) : null}
                            <span>—</span>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {!contactsListPending && !customersInGroup.length && (
                    <div className="p-6 text-sm text-muted-foreground text-center">
                      {debouncedCustomerSearch.trim()
                        ? "No customers match your search in this group."
                        : leadsFilter === "warm"
                          ? "No warm leads in loaded results — try loading more or clear the filter."
                          : leadsFilter === "cold"
                            ? "No cold leads in loaded results — try loading more or clear the filter."
                            : "No customers in this group."}
                    </div>
                  )}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-3 text-xs text-muted-foreground">
                      <svg className="w-4 h-4 mr-1.5 animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                      </svg>
                      Loading more...
                    </div>
                  )}
                  {!contactsHasMore && contactsTotal > 0 && !contactsListPending && (
                    <div className="text-center py-2 text-xs text-[#c0c7d0]">
                      {leadsFilter === "all"
                        ? `${contactsTotal} customer${contactsTotal === 1 ? "" : "s"} total`
                        : `Showing ${customersInGroup.length} ${leadsFilter} of ${customers.length} loaded (${contactsTotal} in group)`}
                    </div>
                  )}
                </div>
              </div>

              {/* Chat window */}
              <div className="col-span-8 flex flex-col overflow-hidden min-h-0">
                {!activeCustomerId ? (
                  <div className="flex-1 flex items-center justify-center bg-muted">
                    <div className="text-center text-muted-foreground">
                      <div className="text-lg font-medium">
                        Select a customer to start chatting
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <ChatHeader
                      title={
                        <span className="flex items-center gap-1.5 min-w-0">
                          {activeCustomer?.premium_member === 1 && (
                            <span
                              data-testid="header-premium-badge"
                              className="inline-block w-3.5 h-3.5 rounded-full bg-amber-400 border border-amber-500 shrink-0"
                              title="Premium"
                            />
                          )}
                          {activeCustomer?.ai_plan === 1 && (
                            <span
                              data-testid="header-ai-badge"
                              className="inline-block w-3.5 h-3.5 rounded-full bg-slate-300 border border-slate-400 shrink-0"
                              title="AI Plan"
                            />
                          )}
                          <span className="truncate">
                            {activeCustomer?.name || "Select a customer"}
                          </span>
                        </span>
                      }
                      subtitle={
                        remoteTyping ? (
                          <span
                            data-testid="typing-indicator"
                            className="text-green-600 font-medium"
                          >
                            typing...
                          </span>
                        ) : (
                          <>
                            {activeCustomer?.online
                              ? "Online"
                              : activeCustomer?.last_seen
                                ? `Last seen ${new Date(activeCustomer.last_seen).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true })}`
                                : "Offline"}
                            {!wsConnected && " • Reconnecting..."}
                          </>
                        )
                      }
                      isConnected={wsConnected}
                      showAdminActions={!!activeCustomer}
                      threadBlocked={threadBlocked}
                      muted={muted}
                      onProfile={
                        activeCustomer
                          ? () =>
                              window.open(
                                `/admin/customers/${activeCustomer._id}`,
                                "_blank",
                              )
                          : undefined
                      }
                      onBlock={async () => {
                        if (
                          !threadBlocked &&
                          !window.confirm(
                            "Block this chat? The customer will not be able to send messages until you unblock.",
                          )
                        )
                          return;
                        try {
                          const res = await chatApi.blockThread(
                            activeThreadId,
                            !threadBlocked,
                          );
                          const thread = res.data?.thread ?? res.data;
                          setThreadBlocked(!!thread?.blocked);
                          setBlockedByName(
                            thread?.blocked
                              ? thread?.blocked_by_name || "Staff"
                              : null,
                          );
                          toast.success(
                            thread?.blocked ? "Chat blocked" : "Chat unblocked",
                          );
                        } catch (e) {
                          toast.error("Failed to update block status");
                        }
                      }}
                      onMute={() => {
                        const next = !muted;
                        setMuted(next);
                        mutedRef.current = next;
                        toast.success(
                          next
                            ? "Notifications muted"
                            : "Notifications unmuted",
                        );
                      }}
                      onDeleteThread={() => {
                        if (
                          !window.confirm(
                            "Are you sure you want to delete this entire chat? This cannot be undone.",
                          )
                        )
                          return;
                        (async () => {
                          try {
                            await chatApi.deleteThread(activeThreadId);
                            toast.success("Chat deleted");
                            setActiveCustomerId(null);
                            setActiveThreadId(null);
                            setMessagesByThread((prev) => {
                              const copy = { ...prev };
                              delete copy[String(activeThreadId)];
                              return copy;
                            });
                            setThreadByCustomerId((prev) => {
                              const copy = { ...prev };
                              delete copy[String(activeCustomerId)];
                              return copy;
                            });
                            broadcastChatActivity();
                          } catch (e) {
                            toast.error(
                              e?.response?.data?.detail ||
                                "Failed to delete chat",
                            );
                          }
                        })();
                      }}
                    />

                    {threadBlocked && (
                      <div
                        data-testid="blocked-notice"
                        className="px-4 py-2 bg-red-50 border-b border-red-200 text-xs text-red-600 font-medium"
                      >
                        Chat blocked by {blockedByName || "Staff"}
                      </div>
                    )}

                    <ChatMessageList
                      listRef={chatListRef}
                      messages={activeMessages}
                      roleThatIsMe="staff"
                      mediaUrl={(id) => chatMediaUrl(id)}
                      showSenderName
                      canManageMessages={!!activeThreadId}
                      onDeleteForMe={async (messageId) => {
                        try {
                          await chatApi.deleteForMe(activeThreadId, messageId);
                          setMessagesByThread((prev) => {
                            const key = String(activeThreadId);
                            const list = (prev[key] || []).filter(
                              (x) => Number(x.id) !== Number(messageId),
                            );
                            return { ...prev, [key]: list };
                          });
                        } catch (e) {
                          toast.error("Failed to delete");
                        }
                      }}
                      onDeleteForAll={async (messageId) => {
                        try {
                          await chatApi.deleteForAll(activeThreadId, messageId);
                        } catch (e) {
                          toast.error("Failed to delete for all");
                        }
                      }}
                      onScroll={() => {
                        const near = isNearBottom();
                        setUserAtBottom(near);
                        if (near) setShowJumpToBottom(false);
                      }}
                      showJumpToBottom={showJumpToBottom}
                      onJumpToBottom={scrollToBottom}
                      emptyMessage="No messages yet. Say hello."
                    />

                    <ChatInput
                      value={draft}
                      onChange={setDraft}
                      onSend={sendText}
                      onAttach={sendAttachment}
                      onVoiceRecorded={sendVoice}
                      placeholder="Message… (send /getrating for app rating card to customer)"
                      disabled={!activeThreadId || threadBlocked}
                      sending={sending}
                      uploadPct={uploadPct}
                      onTyping={() => {
                        const now = Date.now();
                        if (now - lastTypingSentRef.current > 2000) {
                          lastTypingSentRef.current = now;
                          wsSendJson?.({ type: "typing" });
                        }
                      }}
                      inputRef={inputRef}
                    />
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </CompanyAdminLayout>
  );
};

export default ChatsPage;
